import React, { useState } from 'react';
import {
  FiUsers,
  FiSearch,
  FiFilter,
  FiChevronLeft,
  FiChevronRight,
  FiAward,
  FiDollarSign,
  FiCheckCircle,
  FiXCircle,
  FiClock,
  FiTrash2,
  FiAlertCircle,
  FiSquare,
  FiCheckSquare,
  FiLogIn,
} from 'react-icons/fi';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { adminApi } from '../../api';
import { Loader } from '../Common/Loader';
import { ErrorDisplay } from '../Common/ErrorDisplay';
import { User } from '../../types';
import { useApi } from '../../hooks/useApi';
import { showToast } from '../../utils/toast';
import { IoStatsChartSharp } from "react-icons/io5";
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../stores';
import { socketService } from '../../services/socketService';


// Extended User type for admin dashboard with subscription info
interface UserWithSubscription extends User {
  subscriptionStatus?: 'active' | 'expired' | 'none' | 'pending';
  subscription?: {
    status: string;
    isActive: boolean;
    endDate: string;
    daysRemaining: number;
  };
  totalProfitLoss?: number;
}

interface AdminDashboardProps {
  refresh: () => void;
}

const AdminDashboard: React.FC<AdminDashboardProps> = ({ refresh }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { startImpersonation } = useAuthStore();
  const [page, setPage] = useState(1);
  const [roleFilter, setRoleFilter] = useState<string>('');
  const [subscriptionFilter, setSubscriptionFilter] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState('');
  /** Search string sent to the API; only updates when the user clicks Search (or presses Enter). */
  const [appliedSearch, setAppliedSearch] = useState('');
  const [selectedUser, setSelectedUser] = useState<UserWithSubscription | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState<Set<string>>(new Set());
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const [loginAsUserId, setLoginAsUserId] = useState<string | null>(null);

  const applyUserSearch = () => {
    const t = searchQuery.trim();
    setAppliedSearch(t);
    setPage(1);
  };

  const trimmedSearch = appliedSearch.trim();
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['admin', 'users', page, roleFilter, subscriptionFilter, trimmedSearch],
    queryFn: () => adminApi.getAllUsers({
      page,
      limit: 10,
      role: roleFilter || undefined,
      subscription: subscriptionFilter || undefined,
      search: trimmedSearch || undefined,
    }),
  });

  const { data: statsData } = useQuery({
    queryKey: ['admin', 'stats'],
    queryFn: () => adminApi.getStats(),
    refetchOnWindowFocus: true,
    staleTime: 30 * 1000, 
  });

  const users: UserWithSubscription[] = data?.users || [];
  const pagination = data?.pagination;
  const stats = statsData?.stats || {
    totalUsers: 0,
    mastersCount: 0,
    activeSubscriptions: 0,
    totalBalance: 0
  };

  const deleteUserMutation = useApi.useDeleteUser({
    onSuccess: (data) => {
      showToast('success', data.message || 'User deleted successfully');
      setShowDeleteModal(false);
      setSelectedUser(null);
      refetch();
    },
    onError: (err: any) => {
      showToast('error', err.response?.data?.error || 'Failed to delete user');
    },
  });

  const handleDeleteUser = (user: UserWithSubscription) => {
    setSelectedUser(user);
    setShowDeleteModal(true);
  };

  const confirmDeleteUser = () => {
    if (!selectedUser || !selectedUser._id) return;
    deleteUserMutation.mutate(selectedUser._id);
  };

  // Bulk delete handlers
  const handleSelectUser = (userId: string) => {
    setSelectedUsers(prev => {
      const newSet = new Set(prev);
      if (newSet.has(userId)) {
        newSet.delete(userId);
      } else {
        newSet.add(userId);
      }
      return newSet;
    });
  };

  const handleSelectAll = () => {
    const deletableUsers = users.filter(u => u.role !== 'admin' && u._id).map(u => u._id as string);
    if (selectedUsers.size === deletableUsers.length) {
      setSelectedUsers(new Set());
    } else {
      setSelectedUsers(new Set(deletableUsers));
    }
  };

  const handleBulkDelete = () => {
    if (selectedUsers.size === 0) return;
    setShowBulkDeleteModal(true);
  };

  const handleLoginAs = async (user: UserWithSubscription) => {
    if (!user._id) return;
    setLoginAsUserId(user._id);
    try {
      const data = await adminApi.loginAs(user._id);
      if (data.token && data.user) {
        startImpersonation(data.token, data.user);
        // Drop cached API data from the previous user so dashboard/masters load without a manual reload
        queryClient.clear();
        queryClient.setQueryData(['auth', 'me'], { success: true, user: data.user });
        localStorage.removeItem('selectedBrokerId');
        socketService.disconnect();
        showToast('success', `Logged in as ${data.user.name}`);
        navigate('/dashboard');
      }
    } catch (err: any) {
      showToast('error', err.response?.data?.error || 'Failed to log in as user');
    } finally {
      setLoginAsUserId(null);
    }
  };

  const confirmBulkDelete = async () => {
    const userIds = Array.from(selectedUsers);
    let successCount = 0;
    let errorCount = 0;

    for (const userId of userIds) {
      try {
        await adminApi.deleteUser(userId);
        successCount++;
      } catch (error: any) {
        errorCount++;
        console.error(`Failed to delete user ${userId}:`, error);
      }
    }

    setShowBulkDeleteModal(false);
    setSelectedUsers(new Set());
    refetch();
    // Invalidate stats query to refresh dashboard stats
    queryClient.invalidateQueries({ queryKey: ['admin', 'stats'] });

    if (successCount > 0) {
      showToast('success', `Successfully deleted ${successCount} user${successCount > 1 ? 's' : ''}`);
    }
    if (errorCount > 0) {
      showToast('error', `Failed to delete ${errorCount} user${errorCount > 1 ? 's' : ''}`);
    }
  };

  const getRoleBadge = (role?: string | null) => {
    const colors = {
      admin: 'bg-primary/10 text-primary border border-primary/20',
      master: 'bg-primary/10 text-primary border border-primary/20',
      follower: 'bg-surface-elevated text-muted border border-border',
      master_pending: 'bg-warning/10 text-warning border border-warning/20',
    };
    const raw = typeof role === 'string' ? role.trim() : '';
    const badgeClass =
      raw && raw in colors ? colors[raw as keyof typeof colors] : colors.follower;
    const label =
      raw === 'master_pending'
        ? 'Pending Master'
        : raw
          ? raw.charAt(0).toUpperCase() + raw.slice(1).replace(/_/g, ' ')
          : 'Unknown';
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-semibold ${badgeClass}`}>
        {label}
      </span>
    );
  };

  const getSubscriptionBadge = (user: UserWithSubscription) => {
    if (!user.subscriptionStatus || user.subscriptionStatus === 'none') {
      return <span className="text-xs text-muted">No Subscription</span>;
    }

    if (user.subscriptionStatus === 'active') {
      return (
        <span className="flex items-center gap-1 text-xs text-success">
          <FiCheckCircle className="h-3 w-3" />
          Active ({user.subscription?.daysRemaining || 0} days)
        </span>
      );
    }

    if (user.subscriptionStatus === 'expired') {
      return (
        <span className="flex items-center gap-1 text-xs text-danger">
          <FiXCircle className="h-3 w-3" />
          Expired
        </span>
      );
    }

    return (
      <span className="flex items-center gap-1 text-xs text-warning">
        <FiClock className="h-3 w-3" />
        {user.subscriptionStatus}
      </span>
    );
  };

  const handleClearFilters = () => {
    setRoleFilter('');
    setSubscriptionFilter('');
    setSearchQuery('');
    setAppliedSearch('');
    setPage(1);
  };

  if (isLoading) {
    return <Loader message="Loading users..." />;
  }

  if (error) {
    return <ErrorDisplay error="Failed to load users" />;
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="lg:text-4xl text-2xl title-page">
            Admin Dashboard
          </h2>
          <p className="text-muted lg:mt-2 mt-1 lg:text-lg text-[14px]">Manage platform users and system operations</p>
        </div>
      </div>

      {/* Stats Cards – first 3 equal width (slightly wider), 4th card wider */}
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-9 gap-4">
        <div className="card lg:col-span-2">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary/10 rounded-lg">
              <FiUsers className="h-6 w-6 text-primary" />
            </div>
            <div>
              <p className="text-sm text-muted">Total Users</p>
              <p className="lg:text-xl text-xl font-bold text-foreground">{stats.totalUsers}</p>
            </div>
          </div>
        </div>
        <div className="card lg:col-span-2">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary/10 rounded-lg">
              <FiAward className="h-6 w-6 text-primary" />
            </div>
            <div>
              <p className="text-sm text-muted">Masters</p>
              <p className="lg:text-xl text-xl font-bold text-foreground">
                {stats.mastersCount}
              </p>
            </div>
          </div>
        </div>
        <div className="card lg:col-span-2">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-success/10 rounded-lg">
              <FiCheckCircle className="h-6 w-6 text-success" />
            </div>
            <div>
              <p className="text-sm text-muted whitespace-nowrap">Active Subscriptions</p>
              <p className="lg:text-xl text-xl font-bold text-foreground">
                {stats.activeSubscriptions}
              </p>
            </div>
          </div>
        </div>
        <div
          className={`card ${stats.totalBalance > 1000 ? "lg:col-span-3" : "lg:col-span-2"
            }`}
        >
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary/10 rounded-lg">
              <FiDollarSign className="h-6 w-6 text-primary" />
            </div>
            <div>
              <p className="text-sm text-muted">Total Balance</p>
              <p className="lg:text-xl text-xl font-bold text-foreground">
                ${stats.totalBalance.toLocaleString()}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="card">
        <div className="flex flex-col md:flex-row gap-4">
          {/* Search */}
          <div className="flex-1 flex flex-col sm:flex-row gap-2">
            <div className="flex-1 relative min-w-0">
              <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted h-5 w-5" />
              <input
                type="text"
                placeholder="Search by name or email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    applyUserSearch();
                  }
                }}
                className="w-full pl-10 pr-4 py-2 bg-surface border border-border rounded-lg text-foreground placeholder-muted focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
            <button
              type="button"
              onClick={applyUserSearch}
              className="btn-primary shrink-0 px-4 py-2 flex items-center justify-center gap-2 whitespace-nowrap"
            >
              <FiSearch className="h-4 w-4" />
              Search
            </button>
          </div>

          {/* Role Filter */}
          <select
            value={roleFilter}
            onChange={(e) => {
              setRoleFilter(e.target.value);
              setPage(1);
            }}
            className="px-4 py-2 bg-surface border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <option value="">All Roles</option>
            <option value="admin">Admin</option>
            <option value="master">Master</option>
            <option value="follower">Follower</option>
            <option value="master_pending">Pending Master</option>
          </select>

          {/* Subscription Filter */}
          <select
            value={subscriptionFilter}
            onChange={(e) => {
              setSubscriptionFilter(e.target.value);
              setPage(1);
            }}
            className="px-4 py-2 bg-surface border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <option value="">All Subscriptions</option>
            <option value="active">Active</option>
            <option value="expired">Expired</option>
            <option value="none">No Subscription</option>
          </select>

          {/* Clear Filters */}
          {(roleFilter || subscriptionFilter || searchQuery || appliedSearch) && (
            <button
              onClick={handleClearFilters}
              className="px-4 py-2 bg-surface border border-border rounded-lg text-foreground hover:bg-surface-elevated transition-colors flex items-center gap-2"
            >
              <FiFilter className="h-4 w-4" />
              Clear
            </button>
          )}
        </div>
      </div>

      {/* Bulk Actions Bar */}
      {selectedUsers.size > 0 && (
        <div className="card bg-primary/5 border border-primary/20">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="text-sm font-semibold text-primary">
                {selectedUsers.size} user{selectedUsers.size > 1 ? 's' : ''} selected
              </span>
            </div>
            <button
              onClick={handleBulkDelete}
              className="px-4 py-2 bg-danger hover:opacity-90 text-white font-semibold rounded-lg transition-colors flex items-center gap-2"
            >
              <FiTrash2 className="h-4 w-4" />
              Delete Selected
            </button>
          </div>
        </div>
      )}

      {/* Users Table */}
      <div className="card overflow-x-auto">
        <div className="min-w-full">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left py-3 px-4 text-sm font-semibold text-foreground w-12">
                  <button
                    onClick={handleSelectAll}
                    className="p-1 hover:bg-surface-elevated rounded transition-colors"
                    title="Select All"
                  >
                    {users.filter(u => u.role !== 'admin').length > 0 &&
                      selectedUsers.size === users.filter(u => u.role !== 'admin').length ? (
                      <FiCheckSquare className="h-5 w-5 text-primary" />
                    ) : (
                      <FiSquare className="h-5 w-5 text-muted" />
                    )}
                  </button>
                </th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-foreground">User</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-foreground">Role</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-foreground whitespace-nowrap">Trading Type</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-foreground">Subscription</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-foreground">Balance</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-foreground">P&L</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-foreground whitespace-nowrap">Brokerage Connection</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-foreground">Joined</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-foreground">Actions</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-foreground">Statistics</th>
              </tr>
            </thead>
            <tbody>
              {users.filter(u => u.role !== 'admin').length === 0 ? (
                <tr>
                  <td colSpan={10} className="text-center py-12 text-muted">
                    No users found
                  </td>
                </tr>
              ) : (
                users.filter(u => u.role !== 'admin').map((user) => (
                  <tr key={user._id} className="border-b border-border hover:bg-surface-elevated transition-colors">
                    <td className="py-3 px-4">
                      {user.role !== 'admin' && user._id && (
                        <button
                          onClick={() => handleSelectUser(user._id as string)}
                          className="p-1 hover:bg-surface-elevated rounded transition-colors"
                        >
                          {selectedUsers.has(user._id as string) ? (
                            <FiCheckSquare className="h-5 w-5 text-primary" />
                          ) : (
                            <FiSquare className="h-5 w-5 text-muted" />
                          )}
                        </button>
                      )}
                    </td>
                    <td className="py-3 px-4">
                      <div>
                        <p className="font-semibold text-foreground">{user.name}</p>
                        <p className="text-sm text-muted">{user.email}</p>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      {getRoleBadge(user.role)}
                    </td>
                    <td className="py-3 px-4">
                      {user.role === 'master' && user.tradingType ? (
                        <span className={`inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold rounded-full border ${user.tradingType === 'swing'
                          ? 'bg-primary/10 text-primary border-primary/20'
                          : 'bg-primary/10 text-primary border-primary/20'
                          }`}>
                          {user.tradingType === 'swing' ? 'Swing' : 'Day'}
                        </span>
                      ) : (
                        <span className="text-xs text-muted">-</span>
                      )}
                    </td>
                    <td className="py-3 px-4">
                      {getSubscriptionBadge(user)}
                    </td>
                    <td className="py-3 px-4">
                      <span className="text-foreground font-medium">
                      ${(user.hasBrokerageConnected ? (user.accountBalance ?? 0) : 0).toLocaleString()}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      <span className={`font-semibold ${(user.totalProfitLoss || 0) >= 0
                        ? 'text-success'
                        : 'text-danger'
                        }`}>
                        ${(user.totalProfitLoss || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      {user?.hasBrokerageConnected ? (
                        <span className="flex items-center gap-1 text-xs text-green-600 dark:text-green-400">
                          <FiCheckCircle className="h-3 w-3" />
                          Connected
                        </span>
                      ) : (
                        <span className="text-xs text-muted">Not Connected</span>
                      )}
                    </td>
                    <td className="py-3 px-4">
                      <span className="text-sm text-muted">
                        {user.createdAt ? new Date(user.createdAt).toLocaleDateString() : 'N/A'}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => handleLoginAs(user)}
                          disabled={loginAsUserId !== null}
                          className="p-2 text-primary hover:bg-primary/10 rounded-lg transition-colors disabled:opacity-50"
                          title="Login as this user"
                        >
                          <FiLogIn className="h-4 w-4" />
                        </button>
                        {user.role !== 'admin' && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteUser(user);
                            }}
                            className="p-2 text-danger hover:bg-danger/10 rounded-lg transition-colors"
                            title="Delete User"
                          >
                            <FiTrash2 className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      {user.role !== 'admin' && (
                        <button
                          onClick={() => navigate(`/dashboard/statistics?userid=${user._id}`)}
                          className="p-2 text-primary hover:bg-primary/10 rounded-lg transition-colors"
                          title="Check Statistics"
                        >
                          <IoStatsChartSharp className="h-4 w-4" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination && pagination.pages > 1 && (
          <div className="flex items-center justify-between mt-4 pt-4 border-t border-border">
            <div className="text-sm text-muted">
              Showing {(page - 1) * 10 + 1} to {Math.min(page * 10, pagination.total)} of {pagination.total} users
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="p-2 bg-surface border border-border rounded-lg hover:bg-surface-elevated disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <FiChevronLeft className="h-5 w-5" />
              </button>
              <span className="px-4 py-2 text-sm font-medium text-foreground">
                Page {page} of {pagination.pages}
              </span>
              <button
                onClick={() => setPage(p => Math.min(pagination.pages, p + 1))}
                disabled={page === pagination.pages}
                className="p-2 bg-surface border border-border rounded-lg hover:bg-surface-elevated disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <FiChevronRight className="h-5 w-5" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Delete User Modal */}
      {showDeleteModal && selectedUser && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-surface-elevated rounded-xl shadow-2xl max-w-md w-full p-6 border border-border">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-3 bg-danger/10 rounded-full">
                <FiAlertCircle className="h-6 w-6 text-danger" />
              </div>
              <h3 className="text-xl font-bold text-foreground">Delete User</h3>
            </div>

            <p className="text-muted mb-4">
              Are you sure you want to delete this user:{' '}
              <strong className="text-foreground">{selectedUser.name || selectedUser.email}</strong>?
            </p>

            <div className="bg-danger/5 border border-danger/20 rounded-lg p-4 mb-4">
              <p className="text-sm text-red-800 dark:text-red-300">
                <strong>Warning:</strong> This action cannot be undone. All user data, subscriptions, trades, and relationships will be permanently deleted.
              </p>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setSelectedUser(null);
                }}
                className="flex-1 px-4 py-2 border-2 border-border text-foreground font-semibold rounded-lg hover:bg-surface transition-colors"
                disabled={deleteUserMutation.isPending}
              >
                Cancel
              </button>
              <button
                onClick={confirmDeleteUser}
                className="flex-1 px-4 py-2 bg-danger hover:opacity-90 text-white font-semibold rounded-lg transition-colors"
                disabled={deleteUserMutation.isPending}
              >
                {deleteUserMutation.isPending ? 'Deleting...' : 'Confirm Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Delete Modal */}
      {showBulkDeleteModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-surface-elevated rounded-xl shadow-2xl max-w-md w-full p-6 border border-border">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-3 bg-danger/10 rounded-full">
                <FiAlertCircle className="h-6 w-6 text-danger" />
              </div>
              <h3 className="text-xl font-bold text-foreground">Delete Selected Users</h3>
            </div>

            <p className="text-muted mb-4">
              Are you sure you want to <strong className="text-danger">permanently delete</strong> {selectedUsers.size} user{selectedUsers.size > 1 ? 's' : ''}?
            </p>

            <div className="bg-danger/5 border border-danger/20 rounded-lg p-4 mb-4">
              <p className="text-sm text-red-800 dark:text-red-300">
                <strong>Warning:</strong> This action cannot be undone. All user data, subscriptions, trades, and relationships will be permanently deleted.
              </p>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowBulkDeleteModal(false);
                }}
                className="flex-1 px-4 py-2 border-2 border-border text-foreground font-semibold rounded-lg hover:bg-surface transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmBulkDelete}
                className="flex-1 px-4 py-2 bg-danger hover:opacity-90 text-white font-semibold rounded-lg transition-colors"
              >
                Delete {selectedUsers.size} User{selectedUsers.size > 1 ? 's' : ''}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;

